import { useEffect, useRef, type RefObject } from "react";

/** Input level, painted with requestAnimationFrame so audio frames never re-render React. */
export function Meter({ level, active }: { level: RefObject<{ db: number; peak: number }>; active: boolean }) {
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active) {
      if (bar.current) bar.current.style.transform = "scaleX(0)";
      return;
    }
    let raf = 0;
    let shown = 0;
    const paint = () => {
      const { db, peak } = level.current ?? { db: -120, peak: 0 };
      const target = Math.max(0, Math.min(1, (db + 60) / 50));
      shown = target > shown ? target : shown * 0.9 + target * 0.1;
      const el = bar.current;
      if (el) {
        el.style.transform = `scaleX(${shown.toFixed(3)})`;
        el.dataset.zone = peak > 0.97 ? "clip" : peak > 0.6 ? "hot" : "ok";
      }
      raf = requestAnimationFrame(paint);
    };
    raf = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(raf);
  }, [active, level]);

  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
      <div
        ref={bar}
        className="h-full origin-left rounded-full bg-accent transition-colors data-[zone=clip]:bg-danger data-[zone=ok]:bg-fg/70"
        style={{ transform: "scaleX(0)" }}
      />
    </div>
  );
}
